import { Plural, t, Trans } from "@lingui/macro";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, CircularProgress, Stack } from "@mui/material";
import { useAuthState } from "@saleor/sdk";
import React, { useState } from "react";

import { useSnackbar } from "@/components/shared/hooks";
import { cloneAddress } from "@/components/utils/address";
import {
	GmClientFragment,
	ProductVariantFragment,
	useCheckoutDeleteMutation,
	useCreateCheckoutMutation,
	UserDraftCheckoutsDocument,
	useUserCheckoutDeleteMutation,
	useUserDraftCheckoutsQuery,
} from "@/saleor/api";

import useChannelContext from "../shared/contexts/ChannelContext";
import GenericDialog from "../shared/dialog/GenericDialog";
import AddCheckoutClientButton from "./AddCheckoutClientButton";
import ClientTable from "./ClientTable";

export interface SelectedClientsDialogProps {
	onClose: () => void;
	isOpen: boolean;
	clients: GmClientFragment[];
	onShopForClients?: () => void;
	selectedVariant?: ProductVariantFragment | null | undefined;
}

const SelectedClientsDialog: React.VFC<SelectedClientsDialogProps> = ({
	isOpen,
	onClose,
	clients,
	onShopForClients,
	selectedVariant,
}) => {
	const { user } = useAuthState();
	const { channel } = useChannelContext();
	const { data, refetch: refetchCheckouts } = useUserDraftCheckoutsQuery({
		variables: { first: 100, channel, multipleCheckout: false },
	});
	const [userCheckoutDelete] = useUserCheckoutDeleteMutation();
	const [createCheckout, { loading: clearProcessing }] = useCreateCheckoutMutation({
		refetchQueries: [
			{
				query: UserDraftCheckoutsDocument,
				variables: { first: 100, channel, multipleCheckout: false },
			},
		],
		awaitRefetchQueries: true,
	});
	const [deleteCheckout] = useCheckoutDeleteMutation();

	const snackbar = useSnackbar();
	const [selectedClients, setSelectedClients] = useState<string[]>([]);

	// const handleShopForClient = () => {
	//     onShopForClients && onShopForClients()
	// }

	const handleClearList = async () => {
		const checkouts = data?.userDraftCheckouts?.edges.map((e) => e.node);
		if (!checkouts) {
			return null;
		}
		const firstCheckout = checkouts[0];
		const checkoutLine = firstCheckout.lines ? firstCheckout.lines[0] : null;

		const billingAddress = firstCheckout.billingAddress;
		if (checkoutLine && checkoutLine.variant) {
			await userCheckoutDelete();
			const preferences = checkoutLine.preferences
				?.filter((p) => !!p && !!p.attribute && !!p.value)
				.map((pref) => ({
					attributeId: pref!.attribute!.id,
					preferenceValue: pref!.value!,
				}));
			await createCheckout({
				variables: {
					checkout: {
						channel: channel,
						email: user?.email || "anonymous@example.com",
						lines: [
							{
								quantity: 1,
								variantId: checkoutLine.variant.id,
								preferences,
							},
						],
						billingAddress: cloneAddress(billingAddress),
						selectedDeliveryDate: new Date(),
					},
				},
			});
		} else {
			await userCheckoutDelete({
				refetchQueries: [
					{
						query: UserDraftCheckoutsDocument,
						variables: { first: 100, channel, multipleCheckout: false },
					},
				],
				awaitRefetchQueries: true,
			});
		}
		onClose();
		snackbar.showSnackbar({
			message: t`Selected recipient(s) have been cleared`,
			severity: "success",
		});
	};

	const handleRemoveSelected = async () => {
		const totalCheckouts = data?.userDraftCheckouts?.totalCount || 0;
		if (selectedClients.length === totalCheckouts) {
			await userCheckoutDelete({
				refetchQueries: [
					{
						query: UserDraftCheckoutsDocument,
						variables: { first: 100, channel, multipleCheckout: false },
						fetchPolicy: "network-only",
					},
				],
				awaitRefetchQueries: true,
			});
		} else {
			const tokenMap =
				data?.userDraftCheckouts?.edges.reduce<{
					[key: string]: string;
				}>((a, c) => {
					if (c.node.profile?.id) {
						a[c.node.profile.id] = c.node.token;
					}
					return a;
				}, {}) || {};
			const tokens = clients
				.map((client) =>
					selectedClients.includes(client.id) ? tokenMap[client.person.id] : null
				)
				.filter((c) => !!c);
			await Promise.all(
				tokens.map((token) => {
					return deleteCheckout({ variables: { token: token! } });
				})
			);
			await refetchCheckouts({ first: 100 });
		}

		snackbar.showSnackbar({
			message: t`Selected recipient(s) have been removed`,
			severity: "success",
		});
		setSelectedClients([]);
	};

	return (
		<GenericDialog
			isOpen={isOpen}
			onClose={onClose}
			title={t`Selected Recipient(s)`}
			maxWidth="lg"
		>
			<Stack>
				<Stack direction="row" justifyContent="space-between">
					<Button
						color="error"
						size="small"
						startIcon={<DeleteIcon />}
						disabled={selectedClients.length === 0 || clearProcessing}
						onClick={handleRemoveSelected}
					>
						<Plural
							id="remove-selected-clients"
							value={selectedClients.length}
							one="Remove Recipient"
							other="Remove Recipients"
						/>
					</Button>
					<AddCheckoutClientButton clients={clients} selectedVariant={selectedVariant} />
				</Stack>
				<ClientTable
					clients={clients}
					selectedIds={selectedClients}
					onSelectedChange={setSelectedClients}
					idIdentity="person.id"
					selectedClients={clients}
				/>

				<Box display="flex" flexDirection="row" justifyContent="flex-end" mt={2}>
					<Button
						variant="outlined"
						color="primary"
						className="ml-6"
						onClick={handleClearList}
						disabled={clearProcessing}
						endIcon={clearProcessing && <CircularProgress size={20} />}
					>
						<Trans>Clear recipient list</Trans>
					</Button>
					<Button variant="contained" onClick={onClose} className="ml-6">
						<Trans>Done</Trans>
					</Button>
				</Box>
			</Stack>
		</GenericDialog>
	);
};

export default SelectedClientsDialog;
